import { TankPlanWrapper, TankPlanInsideWrapper, ScrollContainer, ZoomedTankOverlay, ZoomedTankContainer } from './TankPlanViewer.styles';
import { useStowageStore } from 'src/context/StowageContext';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useEffect, useRef, useMemo } from 'react';
import { isDeckTank } from 'src/utils/stowage/tankUtils';
import { getAllTanks } from 'src/utils/stowage/tankPlanUtils';
import { calculateMinimumTankPlanWidth } from 'src/utils/stowage/tankPlanGridUtils';
import TankContainer from './TankContainer';
import TankPlanView from './TankPlanView';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';

const TankPlanGrid: React.FC = () => {
    const {
        tankPlan,
        tankplanViewWidth,
        tankPlanZoomInScale,
        tankPlanZoomOutScale,
        tankPlanReset,
        setTankPlanReset,
        tankPlanMove,
        zoomedTank,
        setZoomedTank,
        selectedTankPlanCondition,
        mouseClickPosition,
        setMouseClickPosition,
    } = useStowageStore();

    const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

    // Define zoomedTankDimensions at the top level
    const zoomedTankDimensions = {
        width: 300,
        height: 200,
    };

    // Always call usePositionFromClick at the top level, regardless of whether zoomedTank exists
    const zoomedPosition = usePositionFromClick(mouseClickPosition, zoomedTankDimensions);

    const minTankPlanWidth = useMemo(() => {
        if (!tankPlan) return 0;
        const tanks = getAllTanks(tankPlan);
        const nonDeckTanks = tanks.filter((tank) => !isDeckTank(tank));
        const tankDimensions = nonDeckTanks.map((tank) => ({
            tank: tank,
            startFrame: tank.startFrame,
            endFrame: tank.endFrame,
            yStart: tank.yStart,
            width: tank.endFrame - tank.startFrame,
            height: tank.height,
        }));
        return calculateMinimumTankPlanWidth(tankDimensions);
    }, [tankPlan]);

    useEffect(() => {
        if (tankPlanZoomInScale) {
            if (tankPlanZoomInScale) zoomTankPlanIn();
        }
    }, [tankPlanZoomInScale]);

    const zoomTankPlanIn = () => {
        if (transformComponentRef.current && tankPlanZoomInScale) {
            const { centerView } = transformComponentRef.current;
            centerView(tankPlanZoomInScale, 300, 'easeOut');
        }
    };

    useEffect(() => {
        if (tankPlanZoomOutScale) {
            if (tankPlanZoomOutScale) zoomTankPlanOut();
        }
    }, [tankPlanZoomInScale]);

    const zoomTankPlanOut = () => {
        if (transformComponentRef.current && tankPlanZoomInScale) {
            const { centerView } = transformComponentRef.current;
            centerView(tankPlanZoomInScale, 300, 'easeOut');
        }
    };

    useEffect(() => {
        if (tankPlanReset) {
            const { resetTransform } = transformComponentRef.current;
            if (tankPlanReset) {
                resetTransform();
                setTankPlanReset(false);
            }
        }
    }, [tankPlanReset]);

    // Add keyboard handler to close zoomed tank when Escape key is pressed
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && zoomedTank) {
                setZoomedTank(null);
                setMouseClickPosition(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [zoomedTank, setZoomedTank]);

    const renderTanks = () => {
        if (!tankPlan) return null;

        return (
            <TankPlanInsideWrapper containerWidth={tankplanViewWidth} minWidth={minTankPlanWidth}>
                <TankPlanView tankPlanCondition={selectedTankPlanCondition} />
            </TankPlanInsideWrapper>
        );
    };

    const renderZoomedTank = () => {
        if (!zoomedTank) return null;

        const handleOverlayClick = () => {
            setZoomedTank(null);
            setMouseClickPosition(null);
        };

        const handleContainerClick = (e: React.MouseEvent) => {
            // Close the zoomed tank when clicking on the tank container
            setZoomedTank(null);
            setMouseClickPosition(null);
            e.stopPropagation();
        };

        // Use the position calculated by the hook at the top level
        return (
            <ZoomedTankOverlay onClick={handleOverlayClick}>
                <ZoomedTankContainer onClick={handleContainerClick} top={zoomedPosition.top} left={zoomedPosition.left}>
                    <TankContainer tank={zoomedTank} isZoomed={true} tankPlanCondition={selectedTankPlanCondition} />
                </ZoomedTankContainer>
            </ZoomedTankOverlay>
        );
    };

    return (
        <TankPlanWrapper containerWidth={tankplanViewWidth}>
            <ScrollContainer minWidth={minTankPlanWidth} containerWidth={tankplanViewWidth}>
                <TransformWrapper panning={{ allowLeftClickPan: tankPlanMove }} ref={transformComponentRef}>
                    <TransformComponent>{renderTanks()}</TransformComponent>
                </TransformWrapper>
            </ScrollContainer>
            {renderZoomedTank()}
        </TankPlanWrapper>
    );
};

export default TankPlanGrid;
